import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ── Types ────────────────────────────────────────────────────────────

export interface AccountCampaignSummary {
  company_id: string;
  campaign_id: string;
  campaign_name: string;
  platform: string | null;
  campaign_status: string;
  sender_name: string | null;
  contacts_enrolled: number;
  contacts_replied: number;
  meetings_booked: number;
  contacts_bounced: number;
  first_enrolled_at: string | null;
  last_sent_at: string | null;
  last_replied_at: string | null;
  last_accepted_at: string | null;
  total_messages: number;
  messages_sent: number;
  messages_replied: number;
}

export interface AccountCampaignTotals {
  totalCampaigns: number;
  activeCampaigns: number;
  archivedCampaigns: number;
  totalContactsEnrolled: number;
  totalReplies: number;
  totalMeetings: number;
  lastActivityDate: string | null;
}

// ── Hook: Account Campaign Summary ───────────────────────────────────

export function useAccountCampaigns(accountId: string | null) {
  return useQuery({
    queryKey: ["account-campaigns", accountId],
    enabled: !!accountId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_account_campaign_summary")
        .select("*")
        .eq("company_id", accountId)
        .order("first_enrolled_at", { ascending: false });
      if (error) throw error;
      return (data || []) as AccountCampaignSummary[];
    },
  });
}

// ── Hook: Account Campaign Totals (derived) ──────────────────────────

export function useAccountCampaignTotals(accountId: string | null) {
  const { data: campaigns, ...rest } = useAccountCampaigns(accountId);

  const totals: AccountCampaignTotals | null = campaigns
    ? {
        totalCampaigns: campaigns.length,
        activeCampaigns: campaigns.filter((c) => c.campaign_status === "active").length,
        archivedCampaigns: campaigns.filter((c) => c.campaign_status === "archived").length,
        totalContactsEnrolled: campaigns.reduce((sum, c) => sum + c.contacts_enrolled, 0),
        totalReplies: campaigns.reduce((sum, c) => sum + c.contacts_replied, 0),
        totalMeetings: campaigns.reduce((sum, c) => sum + c.meetings_booked, 0),
        lastActivityDate: campaigns.reduce((latest: string | null, c) => {
          const dates = [c.last_sent_at, c.last_replied_at, c.last_accepted_at, c.first_enrolled_at].filter(Boolean) as string[];
          const max = dates.length ? dates.sort().reverse()[0] : null;
          if (!latest) return max;
          if (!max) return latest;
          return max > latest ? max : latest;
        }, null),
      }
    : null;

  return { data: totals, campaigns, ...rest };
}

// ── Hook: Account Campaign Contact Timeline ──────────────────────────

export interface AccountCampaignEvent {
  id: string;
  event_type: "campaign_enrollment" | "outreach_sent" | "outreach_replied" | "connection_accepted" | "meeting_booked" | "pipeline_activity";
  event_date: string;
  title: string;
  detail: string | null;
  contact_name: string | null;
  campaign_name: string | null;
  platform: string | null;
  status: string | null;
}

export function useAccountCampaignTimeline(accountId: string | null) {
  return useQuery({
    queryKey: ["account-campaign-timeline", accountId],
    enabled: !!accountId,
    queryFn: async () => {
      // Fetch campaign_contacts for all contacts at this account
      const { data: contacts } = await (supabase as any)
        .from("contacts")
        .select("id, full_name")
        .eq("company_id", accountId);
      if (!contacts?.length) return [];

      const contactIds = contacts.map((c: any) => c.id);
      const nameMap: Record<string, string> = {};
      contacts.forEach((c: any) => { nameMap[c.id] = c.full_name || "Unknown"; });

      // Fetch campaign_contacts
      const { data: ccRows } = await (supabase as any)
        .from("campaign_contacts")
        .select("id, contact_id, campaign_id, engagement_status, enrolled_at, sent_at, accepted_at, replied_at, meeting_booked, campaigns!inner(name, platform)")
        .in("contact_id", contactIds)
        .order("enrolled_at", { ascending: false })
        .limit(200);

      // Fetch outreach_messages
      const { data: omRows } = await (supabase as any)
        .from("outreach_messages")
        .select("id, contact_id, campaign_id, status, message_type, delivery_channel, sent_at, reply_received_at, reply_sentiment, created_at, campaigns:campaign_id(name)")
        .in("contact_id", contactIds)
        .not("status", "in", "(archived,failed)")
        .order("created_at", { ascending: false })
        .limit(200);

      // Fetch pipeline_activities
      const { data: opps } = await (supabase as any)
        .from("pipeline_board")
        .select("id, name")
        .eq("account_id", accountId);
      let paRows: any[] = [];
      if (opps?.length) {
        const oppIds = opps.map((o: any) => o.id);
        const oppNameMap: Record<string, string> = {};
        opps.forEach((o: any) => { oppNameMap[o.id] = o.name; });
        const { data } = await (supabase as any)
          .from("pipeline_activities")
          .select("*")
          .in("opportunity_id", oppIds)
          .order("activity_date", { ascending: false })
          .limit(50);
        paRows = (data || []).map((a: any) => ({ ...a, opportunity_name: oppNameMap[a.opportunity_id] || "Unknown" }));
      }

      // Build unified timeline
      const events: AccountCampaignEvent[] = [];

      // Campaign enrollments
      (ccRows || []).forEach((row: any) => {
        if (row.enrolled_at) {
          events.push({
            id: `cc-enroll-${row.id}`,
            event_type: "campaign_enrollment",
            event_date: row.enrolled_at,
            title: "Enrolled in campaign",
            detail: row.campaigns?.name || null,
            contact_name: nameMap[row.contact_id] || null,
            campaign_name: row.campaigns?.name || null,
            platform: row.campaigns?.platform || null,
            status: row.engagement_status,
          });
        }
        if (row.accepted_at) {
          events.push({
            id: `cc-accept-${row.id}`,
            event_type: "connection_accepted",
            event_date: row.accepted_at,
            title: "Connection accepted",
            detail: row.campaigns?.name || null,
            contact_name: nameMap[row.contact_id] || null,
            campaign_name: row.campaigns?.name || null,
            platform: "linkedin",
            status: "accepted",
          });
        }
        if (row.replied_at) {
          events.push({
            id: `cc-reply-${row.id}`,
            event_type: "outreach_replied",
            event_date: row.replied_at,
            title: "Reply received",
            detail: row.campaigns?.name || null,
            contact_name: nameMap[row.contact_id] || null,
            campaign_name: row.campaigns?.name || null,
            platform: row.campaigns?.platform || null,
            status: "replied",
          });
        }
        if (row.meeting_booked) {
          events.push({
            id: `cc-meeting-${row.id}`,
            event_type: "meeting_booked",
            event_date: row.replied_at || row.accepted_at || row.enrolled_at,
            title: "Meeting booked",
            detail: row.campaigns?.name || null,
            contact_name: nameMap[row.contact_id] || null,
            campaign_name: row.campaigns?.name || null,
            platform: row.campaigns?.platform || null,
            status: "meeting_booked",
          });
        }
      });

      // Outreach messages sent
      (omRows || []).forEach((row: any) => {
        if (row.sent_at) {
          events.push({
            id: `om-sent-${row.id}`,
            event_type: "outreach_sent",
            event_date: row.sent_at,
            title: `${row.delivery_channel === "email" ? "Email" : "LinkedIn"} sent`,
            detail: row.message_type?.replace(/_/g, " ") || null,
            contact_name: nameMap[row.contact_id] || null,
            campaign_name: row.campaigns?.name || null,
            platform: row.delivery_channel,
            status: row.status,
          });
        }
        if (row.reply_received_at) {
          events.push({
            id: `om-reply-${row.id}`,
            event_type: "outreach_replied",
            event_date: row.reply_received_at,
            title: `Reply received (${row.reply_sentiment || "unknown"})`,
            detail: null,
            contact_name: nameMap[row.contact_id] || null,
            campaign_name: row.campaigns?.name || null,
            platform: row.delivery_channel,
            status: "replied",
          });
        }
      });

      // Pipeline activities
      paRows.forEach((row: any) => {
        events.push({
          id: `pa-${row.id}`,
          event_type: "pipeline_activity",
          event_date: row.activity_date,
          title: row.title || row.activity_type?.replace(/_/g, " "),
          detail: row.description || null,
          contact_name: null,
          campaign_name: null,
          platform: null,
          status: row.activity_type,
        });
      });

      // Sort by date descending
      events.sort((a, b) => new Date(b.event_date).getTime() - new Date(a.event_date).getTime());

      return events;
    },
  });
}
